import type { 
  User, 
  Course, 
  CreateCourseInput, 
  UpdateCourseInput, 
  CourseQueryParams,
  Enrollment,
  EnrollmentQueryParams,
  CreateEnrollmentInput,
  UpdateEnrollmentInput,
  Combo,
  ComboQueryParams,
  CreateComboInput,
  UpdateComboInput,
  ApiResponse
} from '@/types/api'

// Storage keys
const STORAGE_KEYS = {
  USERS: 'mock_users_data',
  COURSES: 'mock_courses_data',
  ENROLLMENTS: 'mock_enrollments_data',
  COMBOS: 'mock_combos_data',
  CONFIG: 'mock_config_data',
  TOKEN: 'mock_access_token_12345'
}

// Import initial data
import initialUsers from '@/data/mock/users.json'
import initialCourses from '@/data/mock/courses.json'
import initialEnrollments from '@/data/mock/enrollments.json'
import initialCombos from '@/data/mock/combos.json'
import initialConfig from '@/data/mock/config.json'

interface MockConfig {
  counters: {
    users: number
    courses: number
    enrollments: number
    combos: number
  }
  settings: {
    mockEnabled: boolean
    apiDelay: {
      min: number
      max: number
    }
    paginationDefault: {
      limit: number
      maxLimit: number
    }
  }
  stats: {
    totalRevenue: number
    averageRating: number
    totalEnrollments: number
    lastUpdated: string
  }
}

class MockStorage {
  private initialized = false

  private async delay(): Promise<void> {
    const config = this.getConfig()
    const min = config.settings.apiDelay.min
    const max = config.settings.apiDelay.max
    const delayTime = Math.floor(Math.random() * (max - min + 1)) + min
    return new Promise(resolve => setTimeout(resolve, delayTime))
  }

  private initializeData() {
    if (this.initialized) return
    
    // Initialize data if not exists
    if (!localStorage.getItem(STORAGE_KEYS.USERS)) {
      localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(initialUsers))
    }
    if (!localStorage.getItem(STORAGE_KEYS.COURSES)) {
      localStorage.setItem(STORAGE_KEYS.COURSES, JSON.stringify(initialCourses))
    }
    if (!localStorage.getItem(STORAGE_KEYS.ENROLLMENTS)) {
      localStorage.setItem(STORAGE_KEYS.ENROLLMENTS, JSON.stringify(initialEnrollments))
    }
    if (!localStorage.getItem(STORAGE_KEYS.COMBOS)) {
      localStorage.setItem(STORAGE_KEYS.COMBOS, JSON.stringify(initialCombos))
    }
    if (!localStorage.getItem(STORAGE_KEYS.CONFIG)) {
      localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(initialConfig))
    }
    
    this.initialized = true
  }

  public getUsers(): User[] {
    this.initializeData()
    const data = localStorage.getItem(STORAGE_KEYS.USERS)
    return data ? JSON.parse(data) : []
  }

  private saveUsers(users: User[]): void {
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users))
  }

  private getCourses(): Course[] {
    this.initializeData()
    const data = localStorage.getItem(STORAGE_KEYS.COURSES)
    return data ? JSON.parse(data) : []
  }

  private saveCourses(courses: Course[]): void {
    localStorage.setItem(STORAGE_KEYS.COURSES, JSON.stringify(courses))
  }

  private getConfig(): MockConfig {
    this.initializeData()
    const data = localStorage.getItem(STORAGE_KEYS.CONFIG)
    return data ? JSON.parse(data) : initialConfig
  }

  private saveConfig(config: MockConfig): void {
    localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(config))
  }

  private generateId(type: 'users' | 'courses'): string {
    const config = this.getConfig()
    config.counters[type] += 1
    this.saveConfig(config)
    return config.counters[type].toString()
  }

  private getUserById(id: string): User | undefined {
    const users = this.getUsers()
    return users.find(user => user.id === id)
  }

  // Course CRUD operations
  async getCoursesWithPagination(params: CourseQueryParams = {}): Promise<{
    data: Course[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    await this.delay()
    
    let courses = this.getCourses()
    
    // Apply filters
    if (params.search) {
      const searchLower = params.search.toLowerCase()
      courses = courses.filter(course => 
        course.title.toLowerCase().includes(searchLower) ||
        course.description.toLowerCase().includes(searchLower) ||
        course.category.toLowerCase().includes(searchLower)
      )
    }
    
    if (params.category) {
      courses = courses.filter(course => course.category === params.category)
    }
    
    if (params.status) {
      courses = courses.filter(course => course.status === params.status)
    }
    
    // Sort by updatedAt (newest first)
    courses.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    
    // Apply pagination
    const page = params.page || 1
    const limit = Math.min(params.limit || 10, 50)
    const total = courses.length
    const totalPages = Math.ceil(total / limit)
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    
    const paginatedCourses = courses.slice(startIndex, endIndex)
    
    return {
      data: paginatedCourses,
      pagination: {
        page,
        limit,
        total,
        totalPages
      }
    }
  }

  async getCourseById(id: string): Promise<Course | null> {
    await this.delay()
    
    const courses = this.getCourses()
    return courses.find(course => course.id === id) || null
  }

  async createCourse(courseData: CreateCourseInput & { teacherId?: string }): Promise<Course> {
    await this.delay()
    
    const courses = this.getCourses()
    const newId = this.generateId('courses')
    const now = new Date().toISOString()
    
    // Get teacher info (default to admin if not specified)
    const teacherId = courseData.teacherId || '1'
    const teacher = this.getUserById(teacherId)
    
    if (!teacher) {
      throw new Error('Instructor not found')
    }
    
    const newCourse: Course = {
      id: newId,
      title: courseData.title,
      description: courseData.description,
      content: courseData.content,
      coverImage: courseData.coverImage || `https://picsum.photos/400/300?random=${newId}`,
      city: courseData.city,
      country: courseData.country,
      price: courseData.price,
      maxStudents: courseData.maxStudents,
      studentCount: 0,
      category: courseData.category,
      tags: courseData.tags || [],
      status: 'draft',
      teacherId,
      teacher,
      outlines: courseData.outlines || [],
      combos: [], // Start with empty combos array
      youWillLearn: courseData.youWillLearn || [],
      requirements: courseData.requirements || [],
      teachingLocations: courseData.teachingLocations || [],
      prohibited: courseData.prohibited || [],
      isBilingual: courseData.isBilingual || false,
      createdAt: now,
      updatedAt: now
    }
    
    courses.push(newCourse)
    this.saveCourses(courses)
    
    return newCourse
  }

  async updateCourse(id: string, courseData: UpdateCourseInput): Promise<Course> {
    await this.delay()
    
    const courses = this.getCourses()
    const courseIndex = courses.findIndex(course => course.id === id)
    
    if (courseIndex === -1) {
      throw new Error('Course not found')
    }
    
    const existingCourse = courses[courseIndex]
    const now = new Date().toISOString()
    
    // Update teacher if teacherId changed
    let teacher = existingCourse.teacher
    if (courseData.teacherId && courseData.teacherId !== existingCourse.teacherId) {
      const newInstructor = this.getUserById(courseData.teacherId)
      if (!newInstructor) {
        throw new Error('Instructor not found')
      }
      teacher = newInstructor
    }
    
    const updatedCourse: Course = {
      ...existingCourse,
      ...courseData,
      teacher,
      updatedAt: now
    }
    
    courses[courseIndex] = updatedCourse
    this.saveCourses(courses)
    
    return updatedCourse
  }

  async deleteCourse(id: string): Promise<boolean> {
    await this.delay()
    
    const courses = this.getCourses()
    const courseIndex = courses.findIndex(course => course.id === id)
    
    if (courseIndex === -1) {
      throw new Error('Course not found')
    }
    
    courses.splice(courseIndex, 1)
    this.saveCourses(courses)
    
    return true
  }

  // User operations
  async getUserProfile(): Promise<User> {
    await this.delay()
    
    // Return admin user for mock
    const users = this.getUsers()
    const adminUser = users.find(user => user.role === 'admin')
    
    if (!adminUser) {
      throw new Error('Admin user not found')
    }
    
    return adminUser
  }

  async getUsersWithPagination(params: {
    search?: string
    role?: string
    status?: string
    page?: number
    limit?: number
  } = {}): Promise<{
    data: User[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    await this.delay()
    
    let users = this.getUsers()
    
    // Apply filters
    if (params.search) {
      const searchLower = params.search.toLowerCase()
      users = users.filter(user => 
        user.firstName.toLowerCase().includes(searchLower) ||
        (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
        (user.username && user.username.toLowerCase().includes(searchLower))
      )
    }
    
    if (params.role && params.role !== 'all') {
      users = users.filter(user => user.role === params.role)
    }
    
    if (params.status && params.status !== 'all') {
      users = users.filter(user => user.status === params.status)
    }
    
    const page = params.page || 1
    const limit = params.limit || 10
    const total = users.length
    const totalPages = Math.ceil(total / limit)
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    const paginatedUsers = users.slice(startIndex, endIndex)
    
    return {
      data: paginatedUsers,
      pagination: {
        page,
        limit,
        total,
        totalPages
      }
    }
  }

  async getUserByIdAsync(id: string): Promise<User> {
    console.log(`[MockStorage] getUserByIdAsync called with ID: ${id}`)
    await this.delay()
    
    const users = this.getUsers()
    console.log(`[MockStorage] Total users in storage: ${users.length}`)
    console.log(`[MockStorage] Available user IDs:`, users.map(u => u.id))
    
    const user = this.getUserById(id)
    if (!user) {
      console.error(`[MockStorage] User not found with ID: ${id}`)
      throw new Error('User not found')
    }
    
    console.log(`[MockStorage] Found user:`, user)
    return user
  }

  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    await this.delay()
    
    const users = this.getUsers()
    const newUser: User = {
      ...userData,
      id: this.generateId('users'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    users.push(newUser)
    this.saveUsers(users)
    
    return newUser
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    await this.delay()
    
    const users = this.getUsers()
    const userIndex = users.findIndex(user => user.id === id)
    
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    const updatedUser: User = {
      ...users[userIndex],
      ...userData,
      id, // Ensure ID doesn't change
      updatedAt: new Date().toISOString()
    }
    
    users[userIndex] = updatedUser
    this.saveUsers(users)
    
    return updatedUser
  }

  async deleteUser(id: string): Promise<boolean> {
    await this.delay()
    
    const users = this.getUsers()
    const userIndex = users.findIndex(user => user.id === id)
    
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    users.splice(userIndex, 1)
    this.saveUsers(users)
    
    return true
  }

  // Enrollment operations
  private getEnrollmentsSync(): Enrollment[] {
    const stored = localStorage.getItem(STORAGE_KEYS.ENROLLMENTS)
    if (stored) {
      return JSON.parse(stored)
    }
    return initialEnrollments as Enrollment[]
  }

  saveEnrollments(enrollments: Enrollment[]): void {
    localStorage.setItem(STORAGE_KEYS.ENROLLMENTS, JSON.stringify(enrollments))
  }

  async getEnrollment(id: string): Promise<Enrollment | null> {
    await this.delay()
    
    const enrollments = this.getEnrollmentsSync()
    const enrollment = enrollments.find(e => e.id === id)
    
    if (enrollment) {
      // Populate related data
      const users = this.getUsers()
      const courses = this.getCourses()
      
      return {
        ...enrollment,
        student: users.find(u => u.id === enrollment.studentId),
        teacher: enrollment.teacherId ? users.find(u => u.id === enrollment.teacherId) : undefined,
        course: courses.find(c => c.id === enrollment.courseId)
      }
    }
    
    return null
  }

  async getEnrollments(params?: EnrollmentQueryParams): Promise<ApiResponse<Enrollment[]>> {
    await this.delay()
    
    let enrollments = this.getEnrollmentsSync()
    const users = this.getUsers()
    const courses = this.getCourses()
    
    // Populate related data
    enrollments = enrollments.map(enrollment => ({
      ...enrollment,
      student: users.find(u => u.id === enrollment.studentId),
      teacher: enrollment.teacherId ? users.find(u => u.id === enrollment.teacherId) : undefined,
      course: courses.find(c => c.id === enrollment.courseId)
    }))
    
    // Apply filters
    if (params?.status) {
      enrollments = enrollments.filter(e => e.status === params.status)
    }
    
    if (params?.studentId) {
      enrollments = enrollments.filter(e => e.studentId === params.studentId)
    }
    
    if (params?.teacherId) {
      enrollments = enrollments.filter(e => e.teacherId === params.teacherId)
    }
    
    if (params?.courseId) {
      enrollments = enrollments.filter(e => e.courseId === params.courseId)
    }
    
    if (params?.search) {
      const searchLower = params.search.toLowerCase()
      enrollments = enrollments.filter(e => 
        e.student?.firstName?.toLowerCase().includes(searchLower) ||
        e.student?.lastName?.toLowerCase().includes(searchLower) ||
        e.student?.email?.toLowerCase().includes(searchLower) ||
        e.course?.title?.toLowerCase().includes(searchLower) ||
        e.packageName.toLowerCase().includes(searchLower) ||
        e.comments?.toLowerCase().includes(searchLower)
      )
    }
    
    // Sort by creation date (newest first)
    enrollments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    
    // Pagination
    const page = params?.page || 1
    const limit = params?.limit || 10
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    const paginatedEnrollments = enrollments.slice(startIndex, endIndex)
    
    return {
      data: paginatedEnrollments,
      pagination: {
        page,
        limit,
        total: enrollments.length,
        totalPages: Math.ceil(enrollments.length / limit)
      }
    }
  }

  async createEnrollment(data: CreateEnrollmentInput): Promise<Enrollment> {
    await this.delay()
    
    const config = this.getConfig()
    const newId = `enrollment-${config.counters.enrollments + 1}`
    
    const newEnrollment: Enrollment = {
      id: newId,
      studentId: data.studentId,
      teacherId: data.teacherId,
      courseId: data.courseId,
      packageName: data.packageName,
      timeSlot: data.timeSlot,
      status: data.status || 'pending',
      comments: data.comments,
      rating: data.rating,
      upVotes: 0,
      downVotes: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    const enrollments = this.getEnrollmentsSync()
    enrollments.push(newEnrollment)
    this.saveEnrollments(enrollments)
    
    // Update counter
    config.counters.enrollments += 1
    this.saveConfig(config)
    
    // Populate related data
    const users = this.getUsers()
    const courses = this.getCourses()
    
    return {
      ...newEnrollment,
      student: users.find(u => u.id === newEnrollment.studentId),
      teacher: newEnrollment.teacherId ? users.find(u => u.id === newEnrollment.teacherId) : undefined,
      course: courses.find(c => c.id === newEnrollment.courseId)
    }
  }

  async updateEnrollment(id: string, data: UpdateEnrollmentInput): Promise<Enrollment | null> {
    await this.delay()
    
    const enrollments = this.getEnrollmentsSync()
    const enrollmentIndex = enrollments.findIndex(e => e.id === id)
    
    if (enrollmentIndex === -1) {
      return null
    }
    
    const updatedEnrollment: Enrollment = {
      ...enrollments[enrollmentIndex],
      ...data,
      updatedAt: new Date().toISOString()
    }
    
    enrollments[enrollmentIndex] = updatedEnrollment
    this.saveEnrollments(enrollments)
    
    // Populate related data
    const users = this.getUsers()
    const courses = this.getCourses()
    
    return {
      ...updatedEnrollment,
      student: users.find(u => u.id === updatedEnrollment.studentId),
      teacher: updatedEnrollment.teacherId ? users.find(u => u.id === updatedEnrollment.teacherId) : undefined,
      course: courses.find(c => c.id === updatedEnrollment.courseId)
    }
  }

  async deleteEnrollment(id: string): Promise<boolean> {
    await this.delay()
    
    const enrollments = this.getEnrollmentsSync()
    const enrollmentIndex = enrollments.findIndex(e => e.id === id)
    
    if (enrollmentIndex === -1) {
      return false
    }
    
    enrollments.splice(enrollmentIndex, 1)
    this.saveEnrollments(enrollments)
    
    return true
  }

  // Stats operations
  async getStats() {
    await this.delay()
    
    const courses = this.getCourses()
    const users = this.getUsers()
    
    const stats = {
      courses: courses.length,
      teachers: users.filter(u => u.role === 'teacher').length,
      students: users.filter(u => u.role === 'student').length,
      admins: users.filter(u => u.role === 'admin').length,
      totalRevenue: courses.reduce((sum, course) => sum + (course.price * course.studentCount), 0),
      avgRating: 4.6 // Mock average rating
    }
    
    return stats
  }

  // Utility methods
  exportData() {
    return {
      users: this.getUsers(),
      courses: this.getCourses(),
      config: this.getConfig()
    }
  }

  importData(data: { users?: User[], courses?: Course[], config?: MockConfig }) {
    if (data.users) {
      this.saveUsers(data.users)
    }
    if (data.courses) {
      this.saveCourses(data.courses)
    }
    if (data.config) {
      this.saveConfig(data.config)
    }
  }

  resetData() {
    localStorage.removeItem(STORAGE_KEYS.USERS)
    localStorage.removeItem(STORAGE_KEYS.COURSES)
    localStorage.removeItem(STORAGE_KEYS.CONFIG)
    this.initialized = false
    this.initializeData()
  }

  // Combo operations
  private getCombos(): Combo[] {
    this.initializeData()
    const data = localStorage.getItem(STORAGE_KEYS.COMBOS)
    return data ? JSON.parse(data) : []
  }

  private saveCombos(combos: Combo[]): void {
    localStorage.setItem(STORAGE_KEYS.COMBOS, JSON.stringify(combos))
  }

  async getCombosWithPagination(params: ComboQueryParams = {}): Promise<{
    data: Combo[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> {
    await this.delay()
    
    let combos = this.getCombos()
    
    // Apply filters
    if (params.search) {
      const searchLower = params.search.toLowerCase()
      combos = combos.filter(combo => 
        combo.comboName.toLowerCase().includes(searchLower) ||
        combo.description.toLowerCase().includes(searchLower)
      )
    }
    
    if (params.teacherId) {
      combos = combos.filter(combo => combo.teacherId === params.teacherId)
    }
    
    if (params.isActive !== undefined) {
      combos = combos.filter(combo => combo.isActive === params.isActive)
    }
    
    // Sort by updatedAt (newest first)
    combos.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    
    // Apply pagination
    const page = params.page || 1
    const limit = Math.min(params.limit || 10, 50)
    const total = combos.length
    const totalPages = Math.ceil(total / limit)
    const startIndex = (page - 1) * limit
    const endIndex = startIndex + limit
    
    const paginatedCombos = combos.slice(startIndex, endIndex)
    
    return {
      data: paginatedCombos,
      pagination: {
        page,
        limit,
        total,
        totalPages
      }
    }
  }

  async getComboById(id: string): Promise<Combo | null> {
    await this.delay()
    
    const combos = this.getCombos()
    return combos.find(combo => combo.id === id) || null
  }

  async createCombo(comboData: CreateComboInput & { teacherId: string }): Promise<Combo> {
    await this.delay()
    
    const combos = this.getCombos()
    const config = this.getConfig()
    const newId = `combo-${config.counters.combos + 1}`
    const now = new Date().toISOString()
    
    const newCombo: Combo = {
      id: newId,
      teacherId: comboData.teacherId,
      comboName: comboData.comboName,
      description: comboData.description,
      price: comboData.price,
      currency: comboData.currency,
      duration: comboData.duration,
      dailyStartTime: comboData.dailyStartTime,
      dailyEndTime: comboData.dailyEndTime,
      isActive: comboData.isActive ?? true,
      createdAt: now,
      updatedAt: now
    }
    
    combos.push(newCombo)
    this.saveCombos(combos)
    
    // Update counter
    config.counters.combos += 1
    this.saveConfig(config)
    
    return newCombo
  }

  async updateCombo(id: string, comboData: UpdateComboInput): Promise<Combo> {
    await this.delay()
    
    const combos = this.getCombos()
    const comboIndex = combos.findIndex(combo => combo.id === id)
    
    if (comboIndex === -1) {
      throw new Error('Combo not found')
    }
    
    const existingCombo = combos[comboIndex]
    const now = new Date().toISOString()
    
    const updatedCombo: Combo = {
      ...existingCombo,
      ...comboData,
      updatedAt: now
    }
    
    combos[comboIndex] = updatedCombo
    this.saveCombos(combos)
    
    return updatedCombo
  }

  async deleteCombo(id: string): Promise<boolean> {
    await this.delay()
    
    const combos = this.getCombos()
    const comboIndex = combos.findIndex(combo => combo.id === id)
    
    if (comboIndex === -1) {
      throw new Error('Combo not found')
    }
    
    combos.splice(comboIndex, 1)
    this.saveCombos(combos)
    
    return true
  }

  clearAllData() {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key)
    })
    this.initialized = false
  }
}

export const mockStorage = new MockStorage()
export { STORAGE_KEYS }